import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
    ArrowRight, Building2, Briefcase, GraduationCap, Landmark,
    UsersRound, Check, Palette, Megaphone, ShieldCheck, Loader2,
    CheckCircle2, BookOpen, Globe, Heart, ArrowRightLeft, Repeat, TrendingUp,
    Sparkles,
} from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import api, { formatApiError } from "@/lib/api";
import partnerCoaching from "@/assets/partner-coaching.webp";

/* ------------------------------------------------------------------ */
/*  Visual tokens — inspired by deinalltagshelfer.de palette          */
/* ------------------------------------------------------------------ */
const PALETTE = {
    gradient: "linear-gradient(35deg, #E7343F 0%, #F9B000 100%)",
    gradientSoft: "linear-gradient(35deg, #FFF1F0 0%, #FFF8E5 100%)",
    amber: "#F9B000",
    red: "#E7343F",
    text: "#5E707F",
    textStrong: "#3D4D5C",
    bg: "#F4F9FF",
};

const TARGET_GROUPS = [
    {
        id: "schulen",
        icon: BookOpen,
        title: "Schulen",
        headline: "Schülerinnen und Schüler erfolgreich auf Praktikum, Ausbildung, duales Studium und Beruf vorbereiten.",
        body: "Bewerbung24.ai unterstützt Schülerinnen und Schüler bei ihren ersten Bewerbungen – von Praktika über Ausbildungsplätze bis hin zu dualen Studiengängen und dem Berufseinstieg.",
        bullets: [
            "Praktikumsbewerbungen",
            "Ausbildungsbewerbungen",
            "Bewerbungen für duale Studiengänge",
            "Bewerbungen für FSJ und BFD",
            "ATS-optimierte Bewerbungsunterlagen",
            "Stärkenanalyse und Kompetenzsichtbarkeit",
            "Förderung digitaler Kompetenzen",
            "Chancengerechter Zugang zu Bildung und Arbeitsmarkt",
        ],
        keywords: "Bewerbung Schule, Ausbildungsbewerbung, Praktikum Bewerbung, duales Studium, Berufseinstieg",
    },
    {
        id: "berufsschulen",
        icon: Briefcase,
        title: "Berufs- & Fachschulen",
        headline: "Berufseinstieg und Karrierechancen erfolgreich gestalten.",
        body: "Bewerbung24.ai unterstützt Auszubildende, Fachschülerinnen und Fachschüler sowie Teilnehmende beruflicher Qualifizierungen beim erfolgreichen Übergang in Beschäftigung, Weiterbildung oder Studium. Die Plattform hilft dabei, professionelle und ATS-optimierte Bewerbungsunterlagen zu erstellen, individuelle Stärken sichtbar zu machen und sich optimal auf den nächsten Karriereschritt vorzubereiten.",
        bullets: [
            "Bewerbungen für den Berufseinstieg nach Abschluss der Ausbildung",
            "Bewerbungen für Praxisphasen, Praktika und Berufseinstieg",
            "Bewerbungen für Weiterbildungen und Studiengänge",
            "ATS-optimierte Lebensläufe und Anschreiben",
            "KI-gestützte Stärkenanalyse",
            "Förderung digitaler Bewerbungs- und Karrierekompetenzen",
            "Mehrsprachige Plattform in 9 Sprachen",
        ],
        keywords: "Berufsschule, Fachschule, Ausbildung Bewerbung, Berufseinstieg, Weiterbildung",
    },
    {
        id: "hochschulen",
        icon: GraduationCap,
        title: "Hochschulen & Universitäten",
        headline: "Karrierechancen für Studierende und Absolventen verbessern.",
        body: "Bewerbung24.ai unterstützt Studierende und Absolventinnen und Absolventen bei der erfolgreichen Vorbereitung auf Praktika, Werkstudententätigkeiten, duale Studienprogramme und den Berufseinstieg. Mit KI-gestützter Bewerbungsoptimierung, ATS-konformen Unterlagen und einer individuellen Stärkenanalyse werden Karrierechancen gezielt verbessert.",
        bullets: [
            "Professionelle Bewerbungen für Praktika, Werkstudentenstellen und Berufseinstieg",
            "ATS-optimierte Lebensläufe und Anschreiben",
            "KI-gestützte Stärkenanalyse zur besseren Positionierung auf dem Arbeitsmarkt",
            "Unterstützung internationaler Studierender beim Einstieg in den deutschen Arbeitsmarkt",
            "Förderung digitaler Bewerbungs- und Karrierekompetenzen",
            "Skalierbare Unterstützung für Career Center und Studierendenservices",
        ],
        keywords: "Hochschule, Universität, Career Center, Werkstudent, Absolvent, Berufseinstieg",
    },
    {
        id: "jobcenter",
        icon: Landmark,
        title: "Jobcenter & Arbeitsagenturen",
        headline: "Mehr Menschen erfolgreich in Arbeit bringen.",
        body: "Bewerbung24.ai unterstützt Jobcenter und Arbeitsagenturen dabei, Arbeitssuchende bei der Erstellung professioneller und ATS-optimierter Bewerbungsunterlagen zu begleiten. Die Plattform stärkt digitale Bewerbungskompetenzen, macht individuelle Stärken sichtbar und unterstützt den Weg zurück in Beschäftigung.",
        bullets: [
            "Unterstützung bei Arbeitsmarktintegration",
            "KI-gestützte Stärkenanalyse",
            "ATS-optimierte Bewerbungsunterlagen",
            "Aktivierung und Eigenverantwortung",
            "Höhere Bewerbungsqualität",
            "Digitale Unterstützung für Arbeitssuchende",
            "Skalierbare Lösung für große Teilnehmerzahlen",
        ],
        keywords: "Jobcenter, Arbeitsagentur, Arbeitsmarktintegration, Arbeitssuchende, Vermittlung",
    },
    {
        id: "bildungstraeger",
        icon: BookOpen,
        title: "Bildungsträger & Weiterbildung",
        headline: "Bewerbungskompetenz und Karrierechancen nachhaltig stärken.",
        body: "Bewerbung24.ai ergänzt Qualifizierungs-, Integrations- und Weiterbildungsangebote durch moderne KI-Technologie. Teilnehmende lernen, professionelle Bewerbungsunterlagen zu erstellen, ihre Stärken sichtbar zu machen und sich erfolgreich auf den Arbeitsmarkt vorzubereiten.",
        bullets: [
            "Ergänzung bestehender Bildungsangebote",
            "Förderung digitaler Kompetenzen",
            "Praxisnahe Arbeitsmarktvorbereitung",
            "Unterstützung bei beruflicher Neuorientierung",
            "ATS-optimierte Bewerbungsunterlagen",
            "Höhere Selbständigkeit der Teilnehmenden",
        ],
        keywords: "Bildungsträger, Weiterbildung, Qualifizierung, Integrationskurs, AZAV",
    },
    {
        id: "unternehmen",
        icon: Building2,
        title: "Unternehmen & HR",
        headline: "Fachkräfte gewinnen. Mitarbeitende entwickeln. Veränderungen erfolgreich begleiten.",
        body: "Bewerbung24.ai unterstützt Unternehmen dabei, Mitarbeitende und Bewerbende in beruflichen Veränderungsprozessen zu begleiten. Die Plattform erstellt ATS-optimierte Bewerbungsunterlagen, analysiert individuelle Stärken und unterstützt Menschen dabei, ihre beruflichen Chancen sichtbar zu machen.",
        bullets: [
            "Recruiting unterstützen: ATS-optimierte Bewerbungsunterlagen erhöhen die Sichtbarkeit qualifizierter Talente",
            "Interne Karriereentwicklung fördern",
            "Beschäftigungsfähigkeit der Mitarbeitenden stärken",
            "Restrukturierungen begleiten",
            "Arbeitgeberattraktivität steigern",
            "Skalierbar für kleine und große Organisationen",
        ],
        keywords: "Recruiting, Personalentwicklung, HR, Talent Management, Inplacement, Outplacement, Transformation",
    },
    {
        id: "outplacement",
        icon: ArrowRightLeft,
        title: "Outplacement & Transfergesellschaften",
        tabTitle: "Outplacement & Transfer\u00ADgesellschaften",
        headline: "Neue Perspektiven schaffen. Übergänge erfolgreich gestalten.",
        body: "Bewerbung24.ai unterstützt Outplacement-Anbieter und Transfergesellschaften dabei, Teilnehmende professionell bei ihrer beruflichen Neuorientierung zu begleiten.",
        bullets: [
            "Schnellere Bewerbungsfähigkeit",
            "KI-gestützte Stärkenanalyse",
            "Eigenständiges Arbeiten der Teilnehmenden",
            "Berater entlasten – mehr Zeit für individuelle Beratung",
            "Höhere Vermittlungschancen durch ATS-Optimierung",
            "Geeignet für Fachkräfte, Führungskräfte, Berufseinsteiger und internationale Talente",
        ],
        keywords: "Outplacement, Transfergesellschaft, berufliche Neuorientierung, Vermittlung",
    },
    {
        id: "coaches",
        icon: UsersRound,
        title: "Coaches & Karriereberater",
        headline: "Persönliche Beratung trifft moderne KI.",
        body: "Bewerbung24.ai unterstützt Coaches, Karriereberaterinnen und Karriereberater dabei, ihre Klienten bei der Erstellung professioneller und ATS-optimierter Bewerbungsunterlagen zu begleiten.",
        bullets: [
            "Mehr Zeit für Coaching, weniger Formatierungsaufwand",
            "Höhere Qualität durch ATS-optimierte Lebensläufe und Anschreiben",
            "Stärken sichtbar machen mit KI-gestützter Analyse",
            "Moderne digitale Werkzeuge ergänzen persönliche Beratung",
            "Skalierbare Betreuung mehrerer Klienten",
            "Zusätzlicher Mehrwert: KI als Ergänzung Coaching-Angebot",
        ],
        keywords: "Coach, Karriereberater, Bewerbungscoach, Jobcoach, Outplacement-Berater",
    },
    {
        id: "international",
        icon: Globe,
        title: "Internationale Fachkräfte",
        headline: "Internationale Talente erfolgreich auf dem deutschen Arbeitsmarkt positionieren.",
        body: "Bewerbung24.ai unterstützt internationale Fachkräfte, Auszubildende, Studierende und Berufseinsteiger dabei, professionelle und ATS-optimierte Bewerbungsunterlagen nach deutschen Standards zu erstellen.",
        bullets: [
            "ATS-optimierte Bewerbungsunterlagen nach deutschen Standards",
            "Unterstützung beim Einstieg in Ausbildung, Studium und Beschäftigung",
            "KI-gestützte Stärkenanalyse",
            "Mehrsprachige Nutzung in 9 Sprachen",
            "Abbau sprachlicher und kultureller Hürden",
            "Professionelle Darstellung vorhandener Kompetenzen",
            "Höhere Chancen auf Vorstellungsgespräche",
        ],
        keywords: "Internationale Fachkräfte, Fachkräfteintegration, deutscher Arbeitsmarkt, Visum, Anerkennung",
    },
];

const MODELS = [
    {
        icon: Palette, key: "whitelabel", title: "White-Label",
        bullet: "Eigenes Branding, eigene Domain — wie eure Plattform.",
        details: ["Eigenes Logo & Akzentfarbe", "Eigene Subdomain", "Per Default Premium-Zugang", "Co-Branding im PDF-Export"],
    },
    {
        icon: Megaphone, key: "affiliate", title: "Affiliate",
        bullet: "Empfehlt das Tool, kassiert pro Premium-User.",
        details: ["20 % Lifetime-Provision pro Abo", "Tracking-Links & Dashboard", "Auszahlung monatlich", "Marketing-Material inklusive"],
    },
];

const USE_CASES_HR = [
    { icon: Building2,      label: "Recruiting" },
    { icon: Repeat,         label: "Inplacement" },
    { icon: TrendingUp,     label: "Talententwicklung" },
    { icon: Heart,          label: "Outplacement" },
    { icon: Globe,          label: "Internationale Fachkräfte" },
    { icon: ArrowRightLeft, label: "Transformation & Restrukturierung" },
];

export default function PartnerPage() {
    return (
        <PublicLayout>
            <PartnerSEO />
            <div style={{ color: PALETTE.text }}>
                <PartnerHero />
                <ExpertiseStrip />
                <UsecasesSection />
                <TargetGroupsSection />
                <ModelsSection />
                <HowItWorksSection />
                <InquiryForm />
            </div>
        </PublicLayout>
    );
}

/* ---------------- SEO ---------------- */
function PartnerSEO() {
    const title = "Bewerbung24.ai für Partner – KI-Bewerbungen für Schulen, Hochschulen, Jobcenter & Unternehmen";
    const desc = "B2B-Partnerschaften mit Bewerbung24.ai — 7 Tage kostenlos testen. KI-gestützte, ATS-optimierte Bewerbungsunterlagen, Stärkenanalyse und mehrsprachige Plattform für Schulen, Hochschulen, Jobcenter, Bildungsträger, Outplacement, Coaches und internationale Fachkräfte. White-Label und Affiliate verfügbar. Made in Switzerland.";
    const url = "https://www.bewerbung24.ai/partner";
    const ld = {
        "@context": "https://schema.org",
        "@graph": [
            { "@type": "Organization", name: "Bewerbung24.ai", url: "https://www.bewerbung24.ai", logo: "https://www.bewerbung24.ai/logo-icon.png", description: "KI-gestützte, ATS-optimierte Bewerbungsunterlagen für Schulen, Hochschulen, Jobcenter, Bildungsträger, Unternehmen, Outplacement, Coaches und internationale Fachkräfte." },
            { "@type": "Service", name: "Bewerbung24.ai für Bildungseinrichtungen, Jobcenter und Unternehmen", provider: { "@type": "Organization", name: "Bewerbung24.ai" }, url, areaServed: "DE", serviceType: "B2B-Karriereplattform mit KI-gestützten Bewerbungsunterlagen",
              audience: ["Schulen", "Berufs- und Fachschulen", "Hochschulen und Universitäten", "Jobcenter und Arbeitsagenturen", "Bildungsträger und Weiterbildungseinrichtungen", "Unternehmen und HR", "Outplacement und Transfergesellschaften", "Coaches und Karriereberater", "Internationale Fachkräfte"]
                .map(t => ({ "@type": "Audience", audienceType: t })) },
            { "@type": "FAQPage", mainEntity: [
                { "@type": "Question", name: "Was ist Bewerbung24.ai für Partner?", acceptedAnswer: { "@type": "Answer", text: "Bewerbung24.ai für Partner ist eine B2B-Lösung für Schulen, Hochschulen, Jobcenter, Bildungsträger, Unternehmen und Coaches. Wir liefern ATS-optimierte, KI-gestützte Bewerbungsunterlagen, Stärkenanalysen und mehrsprachige Karrierewerkzeuge – als Partner-Abo, White-Label-Lösung oder Affiliate-Partnerschaft." }},
                { "@type": "Question", name: "Ist Bewerbung24.ai DSGVO-konform?", acceptedAnswer: { "@type": "Answer", text: "Ja. Bewerbung24.ai ist Made in Switzerland, Daten sind verschlüsselt, jeder Nutzer kann seine Daten jederzeit exportieren oder löschen. Bewerbung24.ai erfüllt die Anforderungen der DSGVO." }},
                { "@type": "Question", name: "Können Partner Bewerbung24.ai kostenlos testen?", acceptedAnswer: { "@type": "Answer", text: "Ja. Partner können Bewerbung24.ai 7 Tage kostenlos und unverbindlich testen. Einfach das Anfrage-Formular ausfüllen — wir schalten den Testzugang frei." }},
                { "@type": "Question", name: "Welche Sprachen werden unterstützt?", acceptedAnswer: { "@type": "Answer", text: "Die Plattform ist mehrsprachig nutzbar in 9 Sprachen — ideal für internationale Studierende, Fachkräfte und Integrationsangebote." }},
            ]},
        ],
    };

    return (
        <Helmet>
            <title>{title}</title>
            <meta name="description" content={desc} />
            <meta name="keywords" content="Bewerbung KI, ATS-optimiert, Bewerbungssoftware Schulen, Hochschulen, Career Center, Jobcenter, Outplacement, Bildungsträger, Fachkräfteintegration, KI-Bewerbung, mehrsprachige Bewerbung, White-Label Karriereplattform, Partner-Abo Bewerbungssoftware" />
            <link rel="canonical" href={url} />
            <meta property="og:type" content="website" />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={desc} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content="https://www.bewerbung24.ai/og-image.jpg" />
            <meta property="og:locale" content="de_DE" />
            <meta name="twitter:card" content="summary_large_image" />
            <script type="application/ld+json">{JSON.stringify(ld)}</script>
            <html lang="de" />
        </Helmet>
    );
}

/* ---------------- Hero ---------------- */
function PartnerHero() {
    return (
        <section className="relative overflow-hidden" data-testid="partner-hero" style={{ background: PALETTE.bg }}>
            {/* big orange decorative split */}
            <div className="absolute top-0 right-0 w-1/2 h-full opacity-90 pointer-events-none" style={{ background: PALETTE.gradient }} />
            {/* decorative curve */}
            <svg className="absolute right-[30%] top-[20%] w-72 h-72 opacity-30 text-white pointer-events-none" viewBox="0 0 200 200" fill="none">
                <path d="M30 100 Q 100 30 170 100 T 30 170" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" />
                <circle cx="170" cy="100" r="4" fill="currentColor" />
            </svg>

            <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-20 lg:pt-28 pb-16 lg:pb-24 grid lg:grid-cols-[1fr_1fr] gap-12 items-center">
                <div>
                    {/* short gradient accent bar */}
                    <div className="h-1.5 w-16 rounded-full mb-7" style={{ background: PALETTE.gradient }} />
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold tracking-[0.15em] uppercase bg-white border border-zinc-200 mb-5" style={{ color: PALETTE.red }} data-testid="partner-trial-badge">
                        <Sparkles size={12} /> 7 Tage kostenlos testen · Made in Switzerland
                    </span>
                    <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight" style={{ color: PALETTE.textStrong }}>
                        <span className="gradient-warm">Verlässliche</span> KI-Bewerbungen
                        <br />für Ihre <span className="gradient-warm">Bildungs&shy;einrichtung</span>.
                    </h1>
                    <p className="mt-7 text-lg leading-relaxed max-w-xl" style={{ color: PALETTE.text }}>
                        <strong style={{ color: PALETTE.textStrong }}>Bewerbung24.ai</strong> vereint moderne KI-Technologie und über
                        20 Jahre Erfahrung in Recruiting, Karriereberatung, Outplacement und Fachkräfteintegration —
                        für Schulen, Hochschulen, Jobcenter, Bildungsträger, Unternehmen und Coaches.
                    </p>
                    <div className="mt-9 flex flex-wrap gap-3">
                        <a href="#partner-inquiry" data-testid="partner-hero-cta-primary" className="group px-7 py-3.5 rounded-full font-semibold text-white inline-flex items-center gap-2 transition-transform hover:-translate-y-0.5 shadow-lg" style={{ background: PALETTE.gradient }}>
                            7 Tage kostenlos testen <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                        </a>
                        <a href="#partner-inquiry" data-testid="partner-hero-cta-secondary" className="px-7 py-3.5 rounded-full font-semibold inline-flex items-center gap-2 transition-colors" style={{ background: "white", color: PALETTE.textStrong, border: "2px solid #E5EBF2" }}>
                            Partnermodelle entdecken
                        </a>
                    </div>
                </div>

                {/* Right: floating photo collage with gradient background */}
                <div className="relative justify-self-center">
                    <div className="relative w-full max-w-md">
                        <div className="absolute -inset-4 rounded-3xl opacity-30 blur-2xl" style={{ background: PALETTE.gradient }} />
                        <img
                            src={partnerCoaching}
                            alt="Bewerbungscoaching mit KI-Unterstützung — Bildungseinrichtung"
                            width="900"
                            height="1125"
                            className="relative rounded-3xl object-cover shadow-2xl"
                            style={{ aspectRatio: "4/5", width: "100%" }}
                            loading="eager"
                        />
                        {/* Floating quote card */}
                        <div className="absolute -bottom-8 -left-8 bg-white rounded-2xl shadow-2xl p-5 max-w-xs border-l-[4px]" style={{ borderLeftColor: PALETTE.red }}>
                            <p className="text-sm font-semibold leading-snug" style={{ color: PALETTE.textStrong }}>
                                &bdquo;Wir verbinden Innovation mit echter Personalpraxis.&ldquo;
                            </p>
                            <p className="mt-2 text-xs" style={{ color: PALETTE.text }}>20+ Jahre Karriere-Expertise</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Inline gradient-text helper */}
            <style>{`
                .gradient-warm {
                    background: ${PALETTE.gradient};
                    -webkit-background-clip: text;
                    background-clip: text;
                    -webkit-text-fill-color: transparent;
                    color: transparent;
                }
                .gradient-warm-bg { background: ${PALETTE.gradient}; }
                .gradient-warm-bg-soft { background: ${PALETTE.gradientSoft}; }
            `}</style>
        </section>
    );
}

/* ---------------- Expertise strip ---------------- */
function ExpertiseStrip() {
    const USPS = [
        "20+ Jahre Personal-, Arbeitsmarkt- und Integrationsexpertise",
        "Professionelle Softwareentwicklung & moderne KI-Technologie",
        "ATS-optimierte Lebensläufe und Anschreiben",
        "KI-gestützte Stärkenanalyse",
        "Mehrsprachige Plattform in 9 Sprachen",
        "Für Praktikum, Ausbildung, Studium und Berufseinstieg",
        "Unterstützung von Arbeitsmarktintegration und Fachkräftesicherung",
        "Geeignet für Schulen, Hochschulen, Jobcenter, Bildungsträger und Unternehmen",
    ];
    return (
        <section className="bg-white border-y border-zinc-100" data-testid="partner-expertise">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
                <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12 lg:gap-16 items-start">
                    <div>
                        <p className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: PALETTE.red }}>Warum Bewerbung24.ai?</p>
                        <h2 className="font-heading mt-4 text-4xl lg:text-5xl font-bold leading-[1.1] tracking-tight" style={{ color: PALETTE.textStrong }}>
                            Software trifft <span className="gradient-warm">Personalpraxis</span>.<br />
                            In einer Plattform.
                        </h2>
                        <p className="mt-6 leading-relaxed" style={{ color: PALETTE.text }}>
                            In die Entwicklung der Plattform fließen Erfahrungen aus der Begleitung von Fach- und Führungskräften,
                            beruflicher Neuorientierung, Hochschullehre, Fachkräftegewinnung und Arbeitsmarktintegration ein.
                            So entsteht eine praxisnahe Lösung, die Menschen dabei unterstützt, ihre Stärken sichtbar zu machen.
                        </p>
                    </div>
                    <ul className="grid sm:grid-cols-2 gap-3" data-testid="partner-usp-list">
                        {USPS.map((u, i) => (
                            <li key={i} className="flex items-start gap-3 p-4 rounded-2xl bg-white border border-zinc-100 hover:shadow-md transition-shadow">
                                <span className="h-7 w-7 rounded-full flex items-center justify-center text-white shrink-0 mt-0.5" style={{ background: PALETTE.gradient }}>
                                    <Check size={14} />
                                </span>
                                <span className="text-sm leading-snug" style={{ color: PALETTE.textStrong }}>{u}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
}

/* ---------------- HR Use Cases ---------------- */
function UsecasesSection() {
    return (
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28" data-testid="partner-usecases">
            <div className="text-center max-w-2xl mx-auto">
                <p className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: PALETTE.red }}>Einsatzbereiche</p>
                <h2 className="font-heading mt-4 text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight" style={{ color: PALETTE.textStrong }}>
                    Vom Recruiting bis<br />zur <span className="gradient-warm">Transformation</span>.
                </h2>
                <p className="mt-4 leading-relaxed" style={{ color: PALETTE.text }}>
                    Personalentwicklung, Inplacement, Talent Management, Outplacement, internationale Fachkräfteintegration —
                    eine Plattform, viele Einsatzbereiche.
                </p>
            </div>
            <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {USE_CASES_HR.map(({ icon: Icon, label }) => (
                    <div key={label} className="group p-6 rounded-3xl bg-white border border-zinc-100 hover:-translate-y-1 transition-all text-center hover:shadow-lg">
                        <div className="mx-auto h-14 w-14 rounded-full flex items-center justify-center" style={{ background: PALETTE.gradientSoft }}>
                            <Icon size={22} strokeWidth={1.5} style={{ color: PALETTE.red }} />
                        </div>
                        <p className="mt-4 text-sm font-semibold" style={{ color: PALETTE.textStrong }}>{label}</p>
                    </div>
                ))}
            </div>
        </section>
    );
}

/* ---------------- Target Groups (interactive tabs) ---------------- */
function TargetGroupsSection() {
    const [activeId, setActiveId] = useState(TARGET_GROUPS[0].id);
    const active = TARGET_GROUPS.find((g) => g.id === activeId) || TARGET_GROUPS[0];

    const handleSelect = (id) => {
        setActiveId(id);
        // smooth scroll the detail panel into view on mobile
        setTimeout(() => {
            const el = document.getElementById("partner-target-detail");
            if (el && window.innerWidth < 1024) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }, 50);
    };

    return (
        <section className="border-y border-zinc-100" data-testid="partner-targets" style={{ background: PALETTE.bg }}>
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
                <div className="text-center max-w-2xl mx-auto">
                    <p className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: PALETTE.red }}>Für wen?</p>
                    <h2 className="font-heading mt-4 text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight" style={{ color: PALETTE.textStrong }}>
                        Neun Zielgruppen.<br />
                        Eine <span className="gradient-warm">Karriereplattform</span>.
                    </h2>
                    <p className="mt-4 leading-relaxed" style={{ color: PALETTE.text }}>
                        Wählen Sie Ihre Zielgruppe — die Details erscheinen sofort darunter.
                    </p>
                </div>

                {/* Compact icon-tab grid (1 row on desktop, 3 cols on tablet, 2 cols on mobile) */}
                <div
                    className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-2.5"
                    data-testid="partner-target-tabs"
                    role="tablist"
                >
                    {TARGET_GROUPS.map((g) => {
                        const isActive = g.id === activeId;
                        return (
                            <button
                                key={g.id}
                                role="tab"
                                aria-selected={isActive}
                                data-testid={`partner-target-tab-${g.id}`}
                                onClick={() => handleSelect(g.id)}
                                className={`group relative p-3.5 rounded-2xl text-left transition-all hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                                    isActive
                                        ? "shadow-xl text-white"
                                        : "bg-white border border-zinc-100 hover:shadow-md hover:border-zinc-200"
                                }`}
                                style={
                                    isActive
                                        ? { background: PALETTE.gradient }
                                        : {}
                                }
                            >
                                <div
                                    className="h-9 w-9 rounded-full flex items-center justify-center mb-2"
                                    style={
                                        isActive
                                            ? { background: "rgba(255,255,255,0.22)" }
                                            : { background: PALETTE.gradientSoft }
                                    }
                                >
                                    <g.icon
                                        size={16}
                                        strokeWidth={1.6}
                                        style={isActive ? { color: "#fff" } : { color: PALETTE.red }}
                                    />
                                </div>
                                <p
                                    className="text-[11px] font-semibold leading-tight break-words hyphens-auto"
                                    lang="de"
                                    style={isActive ? { color: "#fff" } : { color: PALETTE.textStrong }}
                                >
                                    {g.tabTitle || g.title}
                                </p>
                                {isActive && (
                                    <span
                                        aria-hidden="true"
                                        className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-3 h-3 rotate-45"
                                        style={{ background: PALETTE.amber }}
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Selected detail panel */}
                <article
                    id="partner-target-detail"
                    key={active.id}
                    data-testid={`partner-target-${active.id}`}
                    className="mt-8 rounded-3xl bg-white p-7 lg:p-10 shadow-xl partner-detail-fade"
                    style={{ borderTop: `5px solid ${PALETTE.amber}` }}
                >
                    <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8 lg:gap-12 items-start">
                        <div>
                            <div className="flex items-center gap-3 mb-5">
                                <div className="h-14 w-14 rounded-full flex items-center justify-center text-white shrink-0" style={{ background: PALETTE.gradient }}>
                                    <active.icon size={22} strokeWidth={1.5} />
                                </div>
                                <p className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: PALETTE.red }}>{active.title}</p>
                            </div>
                            <h3 className="font-heading text-2xl lg:text-3xl font-bold leading-snug tracking-tight" style={{ color: PALETTE.textStrong }}>{active.headline}</h3>
                            <p className="mt-4 leading-relaxed" style={{ color: PALETTE.text }}>{active.body}</p>
                            <p className="mt-3 text-[11px] italic" style={{ color: PALETTE.amber }}>Suchbegriffe: {active.keywords}</p>
                        </div>
                        <ul className="grid sm:grid-cols-2 gap-2.5 content-start">
                            {active.bullets.map((b, k) => (
                                <li key={k} className="flex items-start gap-2.5 text-sm leading-snug" style={{ color: PALETTE.textStrong }}>
                                    <span className="h-5 w-5 rounded-full flex items-center justify-center text-white shrink-0 mt-0.5" style={{ background: PALETTE.gradient }}>
                                        <Check size={11} />
                                    </span>
                                    {b}
                                </li>
                            ))}
                        </ul>
                    </div>
                </article>

                {/* SEO-friendly hidden full content (so crawlers see all 9 target groups + deep-link anchors) */}
                <div className="sr-only" aria-hidden="true">
                    {TARGET_GROUPS.map((g) => (
                        <article key={g.id} id={`zielgruppe-${g.id}`}>
                            <h3>{g.title}: {g.headline}</h3>
                            <p>{g.body}</p>
                            <ul>{g.bullets.map((b, k) => <li key={k}>{b}</li>)}</ul>
                            <p>Suchbegriffe: {g.keywords}</p>
                        </article>
                    ))}
                </div>

                <style>{`
                    @keyframes partnerDetailFade {
                        from { opacity: 0; transform: translateY(8px); }
                        to   { opacity: 1; transform: translateY(0); }
                    }
                    .partner-detail-fade { animation: partnerDetailFade 280ms ease-out; }
                `}</style>
            </div>
        </section>
    );
}

/* ---------------- Models ---------------- */
function ModelsSection() {
    return (
        <section className="relative overflow-hidden" data-testid="partner-models" style={{ background: PALETTE.gradient }}>
            <div className="absolute inset-0 opacity-15 pointer-events-none" style={{
                backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
                backgroundSize: "32px 32px",
            }} />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28 text-white">
                <div className="text-center max-w-2xl mx-auto">
                    <p className="text-xs uppercase tracking-[0.2em] font-bold opacity-80">Geschäftsmodelle</p>
                    <h2 className="font-heading mt-4 text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight">
                        Zwei Wege, mit uns<br />zusammenzuarbeiten.
                    </h2>
                    <p className="mt-4 text-white/85 leading-relaxed">Kombinierbar und flexibel — beide Modelle könnt ihr <strong className="text-white">7 Tage kostenlos testen</strong>. Passende Abo-Konditionen klären wir gemeinsam im Gespräch.</p>
                </div>
                <div className="mt-14 grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
                    {MODELS.map(({ icon: Icon, title, bullet, details, key }) => (
                        <div key={key} data-testid={`partner-model-${key}`} className="relative p-7 rounded-3xl bg-white text-zinc-700 hover:-translate-y-1 transition-transform shadow-lg">
                            <div className="h-14 w-14 rounded-full flex items-center justify-center text-white mb-5" style={{ background: PALETTE.gradient }}>
                                <Icon size={22} strokeWidth={1.5} />
                            </div>
                            <h3 className="font-heading text-xl font-bold" style={{ color: PALETTE.textStrong }}>{title}</h3>
                            <p className="mt-2 text-sm leading-relaxed" style={{ color: PALETTE.text }}>{bullet}</p>
                            <ul className="mt-5 space-y-2">
                                {details.map((d) => (
                                    <li key={d} className="flex items-start gap-2 text-sm" style={{ color: PALETTE.textStrong }}>
                                        <span className="h-5 w-5 rounded-full flex items-center justify-center text-white shrink-0 mt-0.5" style={{ background: PALETTE.amber }}>
                                            <Check size={11} />
                                        </span>
                                        {d}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------------- How it works ---------------- */
function HowItWorksSection() {
    return (
        <section className="border-y border-zinc-100" style={{ background: PALETTE.bg }}>
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
                <div className="text-center max-w-2xl mx-auto">
                    <p className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: PALETTE.red }}>Ablauf</p>
                    <h2 className="font-heading mt-4 text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight" style={{ color: PALETTE.textStrong }}>
                        In <span className="gradient-warm">3 Schritten</span><br />zum eigenen Karriere-Portal.
                    </h2>
                </div>
                <div className="mt-14 grid md:grid-cols-3 gap-6 relative">
                    {[
                        { step: "01", title: "Demo & Setup", body: "Wir richten euren Partner-Zugang ein und klären gemeinsam das passende Abo-Modell und ggf. White-Label-Branding." },
                        { step: "02", title: "Zugänge bereitstellen", body: "Eure Endkunden — Schüler, Studierende, Teilnehmende, Mitarbeitende — erhalten unkompliziert Zugang zur Plattform." },
                        { step: "03", title: "Tracking & Reporting", body: "Live-Dashboard zeigt Nutzung, aktive Accounts und Erfolgsquote eurer Endkunden." },
                    ].map((s) => (
                        <div key={s.step} className="relative p-8 rounded-3xl bg-white border border-zinc-100 hover:shadow-lg transition-shadow">
                            <div className="absolute -top-5 -left-5 h-14 w-14 rounded-full flex items-center justify-center font-heading font-bold text-white text-lg shadow-lg" style={{ background: PALETTE.gradient }}>
                                {s.step}
                            </div>
                            <h3 className="mt-3 font-heading text-xl font-bold" style={{ color: PALETTE.textStrong }}>{s.title}</h3>
                            <p className="mt-3 text-sm leading-relaxed" style={{ color: PALETTE.text }}>{s.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------------- Inquiry form ---------------- */
function InquiryForm() {
    const ORG_TYPES = [
        { id: "schule", label: "Schule" }, { id: "berufsschule", label: "Berufsschule / Fachschule" },
        { id: "uni", label: "Hochschule / Universität" }, { id: "arbeitsamt", label: "Jobcenter / Arbeitsagentur" },
        { id: "bildungstraeger", label: "Bildungsträger / Weiterbildung" }, { id: "firma", label: "Unternehmen / HR" },
        { id: "outplacement", label: "Outplacement / Transfergesellschaft" }, { id: "coach", label: "Coach / Karriereberater" },
        { id: "other", label: "Sonstiges" },
    ];
    const INTERESTS = [
        { id: "test",       label: "7-Tage-Test" },
        { id: "abo",        label: "Partner-Abo" },
        { id: "whitelabel", label: "White-Label" },
        { id: "affiliate",  label: "Affiliate" },
    ];

    const [form, setForm] = useState({
        org_name: "", contact_name: "", email: "", phone: "",
        org_type: "uni", expected_users: 50, interest: ["test"], message: "",
    });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const toggleInterest = (id) => {
        const has = form.interest.includes(id);
        update("interest", has ? form.interest.filter((i) => i !== id) : [...form.interest, id]);
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.org_name || !form.contact_name || !form.email) {
            setError("Bitte fülle Organisation, Name und E-Mail aus.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/public/partners/inquiry", form);
            setDone(true);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || "Übermittlung fehlgeschlagen, bitte erneut versuchen.");
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <section id="partner-inquiry" className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
                <div className="max-w-xl mx-auto text-center p-10 rounded-3xl bg-white shadow-xl" data-testid="partner-inquiry-success" style={{ borderTop: `5px solid ${PALETTE.red}` }}>
                    <div className="mx-auto h-16 w-16 rounded-full flex items-center justify-center text-white" style={{ background: PALETTE.gradient }}>
                        <CheckCircle2 size={28} />
                    </div>
                    <h2 className="font-heading mt-5 text-3xl font-bold tracking-tight" style={{ color: PALETTE.textStrong }}>Anfrage angekommen.</h2>
                    <p className="mt-3" style={{ color: PALETTE.text }}>Wir melden uns innerhalb von 24 h bei dir und schalten deinen 7-Tage-Testzugang frei — kostenlos und unverbindlich.</p>
                </div>
            </section>
        );
    }

    return (
        <section id="partner-inquiry" className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
            <div className="grid lg:grid-cols-[1fr_1.3fr] gap-12 lg:gap-20 items-start">
                <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: PALETTE.red }}>7 Tage kostenlos testen</p>
                    <h2 className="font-heading mt-4 text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight" style={{ color: PALETTE.textStrong }}>
                        Mitarbeitende stärken.<br />
                        Potenziale <span className="gradient-warm">sichtbar machen</span>.
                    </h2>
                    <p className="mt-5 leading-relaxed" style={{ color: PALETTE.text }}>
                        Fülle das Formular aus — wir schalten deinen <strong style={{ color: PALETTE.textStrong }}>7-Tage-Testzugang</strong> frei und klären gemeinsam das passende Abo-Modell und ggf. White-Label-Branding.
                    </p>
                    <div className="mt-8 space-y-3 text-sm" style={{ color: PALETTE.textStrong }}>
                        <p className="flex items-center gap-2"><ShieldCheck size={16} className="shrink-0" style={{ color: PALETTE.red }} /> 7 Tage kostenlos & unverbindlich testen</p>
                        <p className="flex items-center gap-2"><ShieldCheck size={16} className="shrink-0" style={{ color: PALETTE.red }} /> DSGVO-konform · Made in Switzerland</p>
                        <p className="flex items-center gap-2"><ShieldCheck size={16} className="shrink-0" style={{ color: PALETTE.red }} /> Rahmenverträge möglich</p>
                        <p className="flex items-center gap-2"><ShieldCheck size={16} className="shrink-0" style={{ color: PALETTE.red }} /> Persönlicher Ansprechpartner</p>
                    </div>
                </div>

                <form onSubmit={submit} data-testid="partner-inquiry-form" className="p-7 lg:p-9 rounded-3xl bg-white shadow-xl" style={{ borderTop: `5px solid ${PALETTE.amber}` }}>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field testid="partner-org-name" label="Organisation" value={form.org_name} onChange={(v) => update("org_name", v)} required />
                        <Field testid="partner-contact-name" label="Ansprechpartner" value={form.contact_name} onChange={(v) => update("contact_name", v)} required />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4 mt-4">
                        <Field testid="partner-email" label="E-Mail" type="email" value={form.email} onChange={(v) => update("email", v)} required />
                        <Field testid="partner-phone" label="Telefon (optional)" type="tel" value={form.phone} onChange={(v) => update("phone", v)} />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4 mt-4">
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: PALETTE.text }}>Art der Organisation</label>
                            <select data-testid="partner-org-type" value={form.org_type} onChange={(e) => update("org_type", e.target.value)} className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-zinc-200 outline-none text-sm" style={{ color: PALETTE.textStrong }}>
                                {ORG_TYPES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                            </select>
                        </div>
                        <Field testid="partner-expected-users" label="Geschätzte User" type="number" value={form.expected_users} onChange={(v) => update("expected_users", parseInt(v || "0", 10))} />
                    </div>
                    <div className="mt-4">
                        <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: PALETTE.text }}>Interesse</label>
                        <div className="flex flex-wrap gap-2">
                            {INTERESTS.map((i) => {
                                const on = form.interest.includes(i.id);
                                return (
                                    <button key={i.id} type="button" onClick={() => toggleInterest(i.id)} data-testid={`partner-interest-${i.id}`}
                                        className="px-4 py-2 rounded-full text-sm font-semibold border-2 transition-colors"
                                        style={on
                                            ? { background: PALETTE.gradient, color: "white", borderColor: "transparent" }
                                            : { background: "white", color: PALETTE.textStrong, borderColor: "#E5EBF2" }}>
                                        {i.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="mt-4">
                        <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: PALETTE.text }}>Nachricht (optional)</label>
                        <textarea data-testid="partner-message" value={form.message} onChange={(e) => update("message", e.target.value)} rows={3} className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-zinc-200 outline-none text-sm" style={{ color: PALETTE.textStrong }} />
                    </div>
                    {error && <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="partner-inquiry-error">{error}</p>}
                    <button type="submit" disabled={busy} data-testid="partner-inquiry-submit"
                        className="mt-6 w-full py-3.5 rounded-full font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-60 transition-transform hover:-translate-y-0.5"
                        style={{ background: PALETTE.gradient }}>
                        {busy && <Loader2 size={16} className="animate-spin" />}
                        {busy ? "Wird gesendet..." : "Anfrage absenden"}
                    </button>
                    <p className="mt-3 text-[11px] text-center" style={{ color: PALETTE.text }}>7 Tage kostenlos testen · Wir melden uns innerhalb 24 h · Keine Kreditkarte, keine Verpflichtung.</p>
                </form>
            </div>
        </section>
    );
}

function Field({ label, testid, value, onChange, type = "text", required }) {
    return (
        <div>
            <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: PALETTE.text }}>
                {label}{required && <span style={{ color: PALETTE.red, marginLeft: 2 }}>*</span>}
            </label>
            <input
                data-testid={testid}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-zinc-200 outline-none text-sm focus:border-transparent"
                style={{ color: PALETTE.textStrong }}
                onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${PALETTE.red}`; }}
                onBlur={(e) => { e.currentTarget.style.boxShadow = "none"; }}
            />
        </div>
    );
}
